/**
 * @file        src/lib/tariffs/reference/validation.ts
 * @description Loads and validates the PP 1291 (§I) recycling reference JSON — per-block structural
 *              checks plus the decree's own normative invariants (п.2≡п.4 above 117,68 kW;
 *              п.1≡п.3 above 58,84 kW; preferential 0,17/0,26 only where the decree allows). Runs on
 *              import; throws (naming the offending JSON path) on any breach.
 * @rule        Coefficients live in the JSON, never in TS. Editing numbers here breaks the
 *              provenance chain recorded in the JSON.
 * @rule        Indexation is NEVER computed. Every revision is a column of the decree text, stored
 *              explicitly — x1.2 is wrong in 1 of 160 cells (96,11 -> published 115,34, not 115,332).
 * @universal   knowledge/universals/tools.md#recycling-tariff-reference-pp-1291-section-i
 */

import referenceData from '@/data/tariffs/recycling-pp1291-section1.json';
import type {
  IceVolumeBlockKey,
  ParagraphKey,
  RecyclingBracket,
  Revision,
  RevisionKey,
  ReferenceFile,
} from './types';

// === MODEL & CONSTANTS =================================================

/** The validated reference data. JSON is the single source of truth. */
export const reference = referenceData as unknown as ReferenceFile;

/** Revision keys in file order (oldest first). Data-driven — a new year adds one JSON key. */
export const REVISION_KEYS: readonly RevisionKey[] = Object.keys(reference.revisions);

/** Canonical ordering of the ICE volume blocks. */
export const VOLUME_BLOCK_KEYS: readonly IceVolumeBlockKey[] = [
  'upTo1000',
  'from1000to2000',
  'from2000to3000',
  'from3000to3500',
  'over3500',
];

const EV_PARAGRAPHS = [
  'paragraph_1_ev_series_hybrid_not_personal',
  'paragraph_3_ev_series_hybrid_personal_use',
] as const;
const ICE_PARAGRAPHS = [
  'paragraph_2_ice_not_personal',
  'paragraph_4_ice_personal_use',
] as const;

// Contiguity tolerance: the decree steps brackets by exactly 0.01 kW; allow a hair of
// floating-point slack so a genuine gap (jump ≫ 0.01) trips validation but 0.01 does not.
const STEP_TOLERANCE = 0.011;
// The two thresholds the normative invariants pivot on (also the preferential ceilings shown to users).
export const KW_ICE_PREFERENTIAL_CEIL = 117.68; // above this, п.2 ≡ п.4
export const KW_EV_PREFERENTIAL_CEIL = 58.84; // above this, п.1 ≡ п.3
// The preferential coefficient pair (personal use). Uniquely low — no commercial coefficient is 0.17.
const PREF_NEW = 0.17;
const PREF_OVER3 = 0.26;

// === HELPERS ===========================================================

/** Throw a path-tagged validation/lookup error. */
export function fail(path: string, message: string): never {
  throw new Error(`[recyclingReference] ${path}: ${message}`);
}

/** Resolve the bracket array for a paragraph within a revision (EV = flat; ICE = keyed by block). */
export function getBrackets(
  rev: Revision,
  paragraph: ParagraphKey,
  volumeBlock: IceVolumeBlockKey | null,
): RecyclingBracket[] {
  if (
    paragraph === 'paragraph_1_ev_series_hybrid_not_personal' ||
    paragraph === 'paragraph_3_ev_series_hybrid_personal_use'
  ) {
    return rev[paragraph].brackets;
  }
  if (volumeBlock === null) fail(paragraph, 'requires a volumeBlock (ICE paragraph)');
  const block = rev[paragraph].volumeBlocks[volumeBlock];
  if (!block) fail(`${paragraph}.volumeBlocks.${volumeBlock}`, 'unknown volume block');
  return block;
}

const isPreferential = (b: RecyclingBracket): boolean => b.new === PREF_NEW && b.over3 === PREF_OVER3;

/** Is (paragraph, volumeBlock, index) a cell where the decree places the preferential 0,17/0,26? */
function isAllowedPreferential(
  paragraph: ParagraphKey,
  volumeBlock: IceVolumeBlockKey | null,
  index: number,
): boolean {
  if (paragraph === 'paragraph_3_ev_series_hybrid_personal_use') {
    return volumeBlock === null && index === 0; // first bracket, ≤58,84 kW
  }
  if (paragraph === 'paragraph_4_ice_personal_use') {
    const block = volumeBlock === 'upTo1000' || volumeBlock === 'from1000to2000' || volumeBlock === 'from2000to3000';
    return block && index <= 3; // first 4 brackets, ≤117,68 kW; ≤3000 cc only
  }
  return false;
}

// === VALIDATION ========================================================

/**
 * Validate a single 16-bracket block, throwing (with the offending JSON path) on any breach.
 * @important Enforces: exactly 16 brackets; ascending & contiguous kW ranges; only the last
 *            bracket open-ended (kwTo === null); every coefficient numeric.
 */
function validateBlock(brackets: RecyclingBracket[], path: string): void {
  if (!Array.isArray(brackets) || brackets.length !== 16) {
    fail(path, `expected exactly 16 brackets, got ${Array.isArray(brackets) ? brackets.length : typeof brackets}`);
  }
  brackets.forEach((b, i) => {
    const bp = `${path}[${i}]`;
    const isLast = i === brackets.length - 1;
    if (typeof b.kwFrom !== 'number' || !Number.isFinite(b.kwFrom)) fail(`${bp}.kwFrom`, 'must be numeric');
    if (isLast) {
      if (b.kwTo !== null) fail(`${bp}.kwTo`, 'the last bracket must have kwTo === null');
    } else if (typeof b.kwTo !== 'number' || !Number.isFinite(b.kwTo)) {
      fail(`${bp}.kwTo`, 'must be numeric (only the last bracket may be null)');
    }
    for (const col of ['new', 'over3'] as const) {
      if (typeof b[col] !== 'number' || !Number.isFinite(b[col])) fail(`${bp}.${col}`, 'coefficient is missing or non-numeric');
    }
    if (i > 0) {
      const prevTo = brackets[i - 1].kwTo;
      if (prevTo === null) fail(`${bp}.kwFrom`, 'a non-final bracket cannot have kwTo === null');
      if (b.kwFrom <= prevTo) fail(`${bp}.kwFrom`, `must ascend past the previous kwTo (${prevTo})`);
      if (b.kwFrom > prevTo + STEP_TOLERANCE) fail(`${bp}.kwFrom`, `is not contiguous with the previous kwTo (${prevTo}) — gap detected`);
    }
  });
}

/** Assert two index-aligned tables carry identical coefficients for every bracket above `kwCeil`. */
function assertEqualAbove(
  a: RecyclingBracket[],
  b: RecyclingBracket[],
  kwCeil: number,
  pathA: string,
  pathB: string,
): void {
  a.forEach((x, i) => {
    if (x.kwFrom <= kwCeil) return;
    const y = b[i];
    if (x.new !== y.new || x.over3 !== y.over3) {
      fail(`${pathB}[${i}]`, `invariant breach: must equal ${pathA}[${i}] above ${kwCeil} kW (${x.new}/${x.over3} vs ${y.new}/${y.over3})`);
    }
  });
}

/** Enforce the preferential-placement invariant both ways for one revision. */
function validatePreferentialPlacement(rev: Revision, key: RevisionKey): void {
  const check = (paragraph: ParagraphKey, volumeBlock: IceVolumeBlockKey | null, brackets: RecyclingBracket[]): void => {
    brackets.forEach((b, i) => {
      const allowed = isAllowedPreferential(paragraph, volumeBlock, i);
      const path = `revisions.${key}.${paragraph}${volumeBlock ? `.volumeBlocks.${volumeBlock}` : ''}[${i}]`;
      if (isPreferential(b) && !allowed) fail(path, 'preferential 0,17/0,26 appears where the decree allows none');
      if (allowed && !isPreferential(b)) fail(path, `expected preferential 0,17/0,26 here, got ${b.new}/${b.over3}`);
    });
  };
  for (const p of EV_PARAGRAPHS) check(p, null, rev[p].brackets);
  for (const p of ICE_PARAGRAPHS) for (const vb of VOLUME_BLOCK_KEYS) check(p, vb, rev[p].volumeBlocks[vb]);
}

/** Validate every revision: metadata, all blocks, and the normative invariants (fact 8). */
export function validateReference(): void {
  const base = reference.normativeRules?.baseRateRub;
  if (typeof base !== 'number' || !Number.isFinite(base) || base <= 0) {
    fail('normativeRules.baseRateRub', 'must be a positive number');
  }
  if (!reference.revisions || REVISION_KEYS.length === 0) fail('revisions', 'no revisions present');
  for (const key of REVISION_KEYS) {
    const rev = reference.revisions[key];
    for (const f of ['validFrom', 'validTo', 'label'] as const) {
      if (typeof rev[f] !== 'string') fail(`revisions.${key}.${f}`, 'must be a string');
    }
    for (const p of EV_PARAGRAPHS) validateBlock(rev[p].brackets, `revisions.${key}.${p}.brackets`);
    for (const p of ICE_PARAGRAPHS) {
      for (const vb of VOLUME_BLOCK_KEYS) validateBlock(rev[p].volumeBlocks[vb], `revisions.${key}.${p}.volumeBlocks.${vb}`);
    }
    // Normative invariants — the reference's own self-check (caught the 23,7 / 23,69 error).
    for (const vb of VOLUME_BLOCK_KEYS) {
      assertEqualAbove(
        rev.paragraph_2_ice_not_personal.volumeBlocks[vb],
        rev.paragraph_4_ice_personal_use.volumeBlocks[vb],
        KW_ICE_PREFERENTIAL_CEIL,
        `revisions.${key}.paragraph_2_ice_not_personal.volumeBlocks.${vb}`,
        `revisions.${key}.paragraph_4_ice_personal_use.volumeBlocks.${vb}`,
      );
    }
    assertEqualAbove(
      rev.paragraph_1_ev_series_hybrid_not_personal.brackets,
      rev.paragraph_3_ev_series_hybrid_personal_use.brackets,
      KW_EV_PREFERENTIAL_CEIL,
      `revisions.${key}.paragraph_1_ev_series_hybrid_not_personal.brackets`,
      `revisions.${key}.paragraph_3_ev_series_hybrid_personal_use.brackets`,
    );
    validatePreferentialPlacement(rev, key);
  }
}

validateReference();
